import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Check, Heart, MagnifyingGlass, User, X, ForkKnife, Users, Airplane, MusicNotes } from '@phosphor-icons/react';

const API = '/api';

// Default meal options - will be replaced by database content
const DEFAULT_MEAL_OPTIONS = [
  { id: 'beef', name: 'Beef', nameEs: 'Carne de Res', description: 'Grilled tenderloin with seasonal vegetables' },
  { id: 'chicken', name: 'Chicken', nameEs: 'Pollo', description: 'Herb-roasted chicken with garden vegetables' },
  { id: 'vegetarian', name: 'Vegetarian', nameEs: 'Vegetariano', description: 'Seasonal vegetable medley' },
];

export default function RSVPPage() {
  const [step, setStep] = useState('lookup'); // lookup, form, success
  const [isLoading, setIsLoading] = useState(false);
  const [guestData, setGuestData] = useState(null);
  const [lookupFirstName, setLookupFirstName] = useState('');
  const [lookupLastName, setLookupLastName] = useState('');
  const [plusOnes, setPlusOnes] = useState([]);
  const [mealChoice, setMealChoice] = useState('');
  const [welcomePartyAttending, setWelcomePartyAttending] = useState(null);
  const [beachDayAttending, setBeachDayAttending] = useState(null);
  const [showFlightInfo, setShowFlightInfo] = useState(false);
  const [rsvpSettings, setRsvpSettings] = useState(null);
  const [MEAL_OPTIONS, setMealOptions] = useState(DEFAULT_MEAL_OPTIONS);
  const [flightInfo, setFlightInfo] = useState({
    arrival_date: '',
    arrival_time: '',
    arrival_airline: '',
    arrival_flight: '',
    departure_date: '',
    departure_time: '',
    departure_airline: '',
    departure_flight: '',
    needs_transportation: false
  });
  
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      attending: 'yes',
    }
  });

  const attending = watch('attending');

  // Fetch RSVP settings on mount
  useEffect(() => {
    const fetchRsvpSettings = async () => {
      try {
        const response = await axios.get(`${API}/content/rsvp`);
        if (response.data) {
          setRsvpSettings(response.data);
          // Update meal options if available
          if (response.data.meal_options && response.data.meal_options.length > 0) {
            const formattedOptions = response.data.meal_options.map(opt => ({
              id: opt.id,
              name: opt.name,
              nameEs: opt.name_es,
              description: opt.description
            }));
            setMealOptions(formattedOptions);
          }
        }
      } catch (error) {
        console.log('Using default RSVP settings');
      }
    };
    fetchRsvpSettings();
  }, []);

  // Step 1: Look up guest by name
  const handleLookup = async (e) => {
    e.preventDefault();
    if (!lookupFirstName.trim() || !lookupLastName.trim()) {
      toast.error('Please enter your first and last name');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/invited-guests/lookup`, {
        params: {
          first_name: lookupFirstName.trim(),
          last_name: lookupLastName.trim()
        }
      });
      setGuestData(response.data);
      
      // Pre-fill form if they already RSVP'd
      if (response.data.existing_rsvp) {
        const rsvp = response.data.existing_rsvp;
        setValue('attending', rsvp.attending ? 'yes' : 'no');
        setValue('dietary_restrictions', rsvp.dietary_restrictions || '');
        setValue('message', rsvp.message || '');
        setMealChoice(rsvp.meal_choice || '');
        setWelcomePartyAttending(typeof rsvp.attending_welcome_party === 'boolean' ? rsvp.attending_welcome_party : null);
        setBeachDayAttending(typeof rsvp.attending_beach_day === 'boolean' ? rsvp.attending_beach_day : null);
        setPlusOnes(rsvp.plus_ones || []);
      } else {
        setWelcomePartyAttending(null);
        setBeachDayAttending(null);
        // Pre-populate with household members from address collection
        const householdMembers = response.data.household_members || [];
        if (householdMembers.length > 0) {
          const prefilledPlusOnes = householdMembers.map(member => ({
            name: member.name || '',
            dietary_restrictions: '',
            meal_choice: '',
            attending: true,
            attending_welcome_party: null,
            attending_beach_day: null,
            song_request: '',
            from_household: true // Mark as pre-filled from household
          }));
          setPlusOnes(prefilledPlusOnes);
        } else {
          setPlusOnes([]);
        }
      }
      
      setStep('form');
      toast.success(`Welcome, ${response.data.first_name}!`);
    } catch (error) {
      console.error('Lookup error:', error);
      toast.error(error.response?.data?.detail || 'Name not found. Please check your spelling or contact Stefanie & Walter.');
    } finally {
      setIsLoading(false);
    }
  };

  // Add a plus one
  const addPlusOne = () => {
    if (plusOnes.length < guestData.plus_ones_allowed) {
      setPlusOnes([...plusOnes, { name: '', dietary_restrictions: '', meal_choice: '', attending: true, attending_welcome_party: null, attending_beach_day: null, song_request: '' }]);
    }
  };

  // Remove a plus one
  const removePlusOne = (index) => {
    setPlusOnes(plusOnes.filter((_, i) => i !== index));
  };

  // Update plus one info
  const updatePlusOne = (index, field, value) => {
    const updated = [...plusOnes];
    updated[index][field] = value;
    setPlusOnes(updated);
  };

  // Step 2: Submit RSVP
  const onSubmit = async (data) => {
    if (attending === 'yes') {
      // Validate plus ones have names
      for (const po of plusOnes) {
        if (!po.name.trim()) {
          toast.error('Please enter names for all guests');
          return;
        }
      }
      // Validate meal choice
      if (!mealChoice) {
        toast.error('Please select your meal preference');
        return;
      }
      // Validate welcome party answers
      if (welcomePartyAttending === null) {
        toast.error('Please let us know if you will attend the Welcome Party / Por favor indícanos si asistirás a la fiesta de bienvenida');
        return;
      }
      if (beachDayAttending === null) {
        toast.error('Please let us know if you will join the Beach Day in El Tunco / Por favor indícanos si asistirás al día de playa en El Tunco');
        return;
      }
      for (const po of plusOnes) {
        if (po.attending !== false && typeof po.attending_welcome_party !== 'boolean') {
          toast.error(`Please answer the Welcome Party question for ${po.name || 'each guest'}`);
          return;
        }
        if (po.attending !== false && typeof po.attending_beach_day !== 'boolean') {
          toast.error(`Please answer the Beach Day question for ${po.name || 'each guest'}`);
          return;
        }
      }
    }

    setIsLoading(true);
    try {
      const payload = {
        guest_id: guestData.id,
        first_name: guestData.first_name,
        last_name: guestData.last_name,
        email: guestData.email,
        attending: data.attending === 'yes',
        attending_welcome_party: data.attending === 'yes' ? welcomePartyAttending : false,
        attending_beach_day: data.attending === 'yes' ? beachDayAttending : false,
        plus_ones: data.attending === 'yes' ? plusOnes.map(po => ({
          name: po.name,
          attending: po.attending !== false, // default to true if not specified
          attending_welcome_party: po.attending === false ? false : po.attending_welcome_party,
          attending_beach_day: po.attending === false ? false : po.attending_beach_day,
          dietary_restrictions: po.dietary_restrictions || '',
          meal_choice: po.meal_choice || '',
          song_request: po.song_request || ''
        })) : [],
        dietary_restrictions: data.dietary_restrictions || null,
        meal_choice: data.attending === 'yes' ? mealChoice : null,
        message: data.message || null,
        song_request: data.song_request || null,
        flight_info: (data.attending === 'yes' && showFlightInfo && flightInfo.arrival_date) ? flightInfo : null,
      };

      await axios.post(`${API}/rsvp`, payload);
      setStep('success');
      toast.success('Thank you for your RSVP!');
    } catch (error) {
      console.error('RSVP error:', error);
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Lookup Step
  if (step === 'lookup') {
    return (
      <div data-testid="rsvp-lookup" className="min-h-screen bg-stone-50">
        <div className="page-header bg-white border-b border-stone-200">
          <div className="max-w-4xl mx-auto px-4">
            <p className="font-body text-sm tracking-[0.2em] uppercase mb-4" style={{ color: "#B8A88A" }}>
              We Hope You Can Make It
            </p>
            <p className="font-body text-xs italic text-stone-400 -mt-3 mb-4">
              Esperamos Que Puedas Asistir
            </p>
            <h1 className="page-title">RSVP</h1>
            <p className="font-body text-xs italic text-stone-400">Confirmar Asistencia</p>
            <p className="page-subtitle mt-4">
              Please respond by {rsvpSettings?.deadline || 'October 16, 2026'}
            </p>
            <p className="font-body text-xs italic text-stone-400">
              Por favor responde antes del {rsvpSettings?.deadline_es || '16 de Octubre de 2026'}
            </p>
          </div>
        </div>

        <div className="py-16 md:py-24">
          <div className="max-w-md mx-auto px-4">
            <div className="bg-white border border-stone-200 p-8">
              <div className="text-center mb-8">
                <MagnifyingGlass size={48} weight="light" className="text-[#B8A88A] mx-auto mb-4" />
                <h2 className="font-display text-2xl text-stone-900 mb-1">
                  Find Your Invitation
                </h2>
                <p className="font-body text-xs italic text-stone-400 mb-2">
                  Encuentra Tu Invitación
                </p>
                <p className="font-body text-sm text-stone-500">
                  Enter your name as it appears on your invitation
                </p>
                <p className="font-body text-xs italic text-stone-400">
                  Ingresa tu nombre como aparece en tu invitación
                </p>
              </div>

              <form onSubmit={handleLookup} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="font-body text-sm text-stone-700">
                    First Name <span className="italic text-stone-400 text-xs">/ Nombre</span>
                  </Label>
                  <Input
                    id="first_name"
                    data-testid="lookup-first-name"
                    value={lookupFirstName}
                    onChange={(e) => setLookupFirstName(e.target.value)}
                    className="bg-white border-stone-200 focus:border-[#C9B99A] focus:ring-[#C9B99A]"
                    placeholder="Your first name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="last_name" className="font-body text-sm text-stone-700">
                    Last Name <span className="italic text-stone-400 text-xs">/ Apellido</span>
                  </Label>
                  <Input
                    id="last_name"
                    data-testid="lookup-last-name"
                    value={lookupLastName}
                    onChange={(e) => setLookupLastName(e.target.value)}
                    className="bg-white border-stone-200 focus:border-[#C9B99A] focus:ring-[#C9B99A]"
                    placeholder="Your last name"
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="lookup-btn"
                  disabled={isLoading}
                  className="w-full bg-[#C9B99A] hover:bg-[#B8A88A] text-white py-6"
                >
                  {isLoading ? 'Looking up... / Buscando...' : 'Find My Invitation / Buscar Mi Invitación'}
                </Button>
              </form>

              <p className="font-body text-xs text-stone-400 text-center mt-6">
                Can't find your invitation? Contact us at SWRodriguezWedding2026@gmail.com
              </p>
              <p className="font-body text-xs italic text-stone-400 text-center">
                ¿No encuentras tu invitación? Contáctanos
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success Step
  if (step === 'success') {
    return (
      <div data-testid="rsvp-success" className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-[#F0EBE3] rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={32} weight="bold" className="text-[#B8A88A]" />
          </div>
          <h1 className="font-display text-3xl text-stone-900 mb-2">
            Thank You, {guestData?.first_name}!
          </h1>
          <p className="font-body text-sm italic text-stone-400 mb-4">
            ¡Gracias, {guestData?.first_name}!
          </p>
          <p className="font-body text-stone-600 mb-2">
            {attending === 'yes' 
              ? "We're so excited to celebrate with you!"
              : "We're sorry you can't make it, but we appreciate you letting us know!"}
          </p>
          <p className="font-body text-sm italic text-stone-400 mb-8">
            {attending === 'yes' 
              ? "¡Estamos emocionados de celebrar contigo!"
              : "Lamentamos que no puedas asistir, ¡pero agradecemos que nos hayas informado!"}
          </p>
          <a
            href="/"
            className="inline-block bg-[#C9B99A] text-white px-8 py-3 font-body font-medium
                       transition-all duration-300 hover:bg-[#B8A88A]"
          >
            Back to Home / Volver al Inicio
          </a>
        </div>
      </div>
    );
  }

  // Form Step
  return (
    <div data-testid="rsvp-page" className="min-h-screen bg-stone-50">
      <div className="page-header bg-white border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4">
          <p className="font-body text-sm tracking-[0.2em] uppercase mb-4" style={{ color: "#B8A88A" }}>
            {guestData?.has_rsvped ? 'Update Your Response' : 'You\'re Invited!'}
          </p>
          <p className="font-body text-xs italic text-stone-400 -mt-3 mb-4">
            {guestData?.has_rsvped ? 'Actualiza Tu Respuesta' : '¡Estás Invitado!'}
          </p>
          <h1 className="page-title">
            Welcome, {guestData?.first_name}!
          </h1>
          <p className="font-body text-xs italic text-stone-400">
            ¡Bienvenido, {guestData?.first_name}!
          </p>
          <p className="page-subtitle mt-4">
            {guestData?.plus_ones_allowed > 0 
              ? `You may bring up to ${guestData.plus_ones_allowed} guest${guestData.plus_ones_allowed > 1 ? 's' : ''}`
              : 'This invitation is for you only'}
          </p>
          <p className="font-body text-xs italic text-stone-400">
            {guestData?.plus_ones_allowed > 0 
              ? `Puedes traer hasta ${guestData.plus_ones_allowed} invitado${guestData.plus_ones_allowed > 1 ? 's' : ''}`
              : 'Esta invitación es solo para ti'}
          </p>
        </div>
      </div>

      <div className="py-16 md:py-24">
        <div className="max-w-xl mx-auto px-4">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            
            {/* Overall Attendance Question */}
            <div className="bg-white border border-stone-200 p-6 space-y-4">
              <Label className="font-body text-sm text-stone-700">
                Will your household be attending the wedding? * <span className="italic text-stone-400 text-xs">/ ¿Asistirá tu hogar a la boda?</span>
              </Label>
              <RadioGroup
                defaultValue={guestData?.existing_rsvp?.attending ? 'yes' : 'yes'}
                onValueChange={(value) => setValue('attending', value)}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="attending-yes" data-testid="radio-attending-yes" />
                  <Label htmlFor="attending-yes" className="font-body cursor-pointer">
                    Joyfully Accept <span className="italic text-stone-400 text-xs">/ Acepto con Gusto</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="attending-no" data-testid="radio-attending-no" />
                  <Label htmlFor="attending-no" className="font-body cursor-pointer">
                    Regretfully Decline <span className="italic text-stone-400 text-xs">/ Declino</span>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Beach Day Info */}
            {attending === 'yes' && (
              <div data-testid="beach-day-info" className="bg-[#FAF8F5] border border-[#C9B99A] p-6 space-y-2">
                <h3 className="font-display text-lg text-stone-900">Beach Day & Overnight in El Tunco — Saturday, Dec 12</h3>
                <p className="font-body text-xs italic text-stone-400">Día de Playa y Noche en El Tunco — Sábado, 12 de Diciembre</p>
                <p className="font-body text-sm text-stone-600">
                  Join us as we continue the celebration at the beach! Buses will depart from Cardedeu Suites in San Salvador on Saturday afternoon and take guests to El Tunco, where we'll spend the night. If you are staying in El Salvador through Sunday or Monday, please let us know in your RSVP so we can plan transportation accordingly.
                </p>
                <p className="font-body text-xs italic text-stone-400">
                  ¡Acompáñennos a continuar la celebración en la playa! El sábado por la tarde, saldrán buses desde Cardedeu Suites en San Salvador hacia El Tunco, donde pasaremos la noche. Si se quedarán en El Salvador hasta el domingo o lunes, por favor indíquenlo en su RSVP para ayudarnos a coordinar el transporte.
                </p>
              </div>
            )}

            {/* Main Guest Card - The person who looked up */}
            {attending === 'yes' && (
              <div className="bg-white border-2 border-[#C9B99A] p-6 space-y-4">
                <div className="flex items-center gap-4 pb-4 border-b border-stone-100">
                  <div className="w-12 h-12 bg-[#C9B99A] rounded-full flex items-center justify-center">
                    <User size={24} weight="light" className="text-white" />
                  </div>
                  <div>
                    <p className="font-display text-lg text-stone-900">
                      {guestData?.first_name} {guestData?.last_name}
                    </p>
                    <p className="font-body text-xs text-[#B8A88A]">Primary Guest / Invitado Principal</p>
                  </div>
                </div>

                {/* Welcome Party for Main Guest */}
                <div className="flex items-center gap-3 p-3 bg-[#FAF8F5] border border-[#E8E0D0] rounded">
                  <div className="flex-1">
                    <Label className="font-body text-sm text-stone-700">
                      Will you attend the Welcome Party on Dec 10? *
                    </Label>
                    <p className="font-body text-xs italic text-stone-400">¿Asistirás a la fiesta de bienvenida el 10 de diciembre?</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      data-testid="main-guest-welcome-yes"
                      onClick={() => setWelcomePartyAttending(true)}
                      className={`px-4 py-1.5 text-sm font-body rounded transition-colors ${
                        welcomePartyAttending === true
                          ? 'bg-[#C9B99A] text-white'
                          : 'bg-white border border-stone-200 text-stone-600 hover:border-[#C9B99A]'
                      }`}
                    >
                      Yes / Sí
                    </button>
                    <button
                      type="button"
                      data-testid="main-guest-welcome-no"
                      onClick={() => setWelcomePartyAttending(false)}
                      className={`px-4 py-1.5 text-sm font-body rounded transition-colors ${
                        welcomePartyAttending === false
                          ? 'bg-stone-500 text-white'
                          : 'bg-white border border-stone-200 text-stone-600 hover:border-stone-400'
                      }`}
                    >
                      No
                    </button>
                  </div>
                </div>

                {/* Beach Day for Main Guest */}
                <div className="flex items-center gap-3 p-3 bg-[#FAF8F5] border border-[#E8E0D0] rounded">
                  <div className="flex-1">
                    <Label className="font-body text-sm text-stone-700">
                      Will you join the Beach Day in El Tunco on Sat, Dec 12? *
                    </Label>
                    <p className="font-body text-xs italic text-stone-400">¿Asistirás al día de playa en El Tunco el sábado 12 de diciembre?</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      data-testid="main-guest-beach-yes"
                      onClick={() => setBeachDayAttending(true)}
                      className={`px-4 py-1.5 text-sm font-body rounded transition-colors ${
                        beachDayAttending === true
                          ? 'bg-[#C9B99A] text-white'
                          : 'bg-white border border-stone-200 text-stone-600 hover:border-[#C9B99A]'
                      }`}
                    >
                      Yes / Sí
                    </button>
                    <button
                      type="button"
                      data-testid="main-guest-beach-no"
                      onClick={() => setBeachDayAttending(false)}
                      className={`px-4 py-1.5 text-sm font-body rounded transition-colors ${
                        beachDayAttending === false
                          ? 'bg-stone-500 text-white'
                          : 'bg-white border border-stone-200 text-stone-600 hover:border-stone-400'
                      }`}
                    >
                      No
                    </button>
                  </div>
                </div>

                {/* Meal Selection for Main Guest */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <ForkKnife size={18} weight="light" className="text-[#B8A88A]" />
                    <Label className="font-body text-sm text-stone-700">
                      Meal Preference * <span className="italic text-stone-400 text-xs">/ Preferencia de Comida</span>
                    </Label>
                  </div>
                  <select
                    data-testid="main-guest-meal"
                    value={mealChoice}
                    onChange={(e) => setMealChoice(e.target.value)}
                    className="w-full p-3 border border-stone-200 bg-white text-stone-700 font-body text-sm focus:border-[#C9B99A] focus:outline-none rounded"
                  >
                    <option value="">Select meal... / Seleccionar comida...</option>
                    {MEAL_OPTIONS.map((meal) => (
                      <option key={meal.id} value={meal.id}>
                        {meal.name} / {meal.nameEs} - {meal.description}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Dietary Restrictions for Main Guest */}
                <div className="space-y-2">
                  <Label className="font-body text-sm text-stone-700">
                    Dietary Restrictions <span className="italic text-stone-400 text-xs">/ Restricciones Dietéticas (Opcional)</span>
                  </Label>
                  <Input
                    data-testid="input-dietary"
                    {...register('dietary_restrictions')}
                    className="bg-white border-stone-200 focus:border-[#C9B99A] focus:ring-[#C9B99A]"
                    placeholder="e.g., Vegetarian, Gluten-free, Nut allergy"
                  />
                </div>

                {/* Song Request for Main Guest */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <MusicNotes size={18} weight="light" className="text-[#B8A88A]" />
                    <Label className="font-body text-sm text-stone-700">
                      Song Request <span className="italic text-stone-400 text-xs">/ Canción que Quieres Escuchar (Opcional)</span>
                    </Label>
                  </div>
                  <Input
                    data-testid="input-song"
                    {...register('song_request')}
                    className="bg-white border-stone-200 focus:border-[#C9B99A] focus:ring-[#C9B99A]"
                    placeholder="What song will get you on the dance floor?"
                  />
                </div>
              </div>
            )}

            {/* Household Members / Plus Ones */}
            {attending === 'yes' && guestData?.plus_ones_allowed > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users size={20} className="text-[#B8A88A]" />
                    <div>
                      <Label className="font-body text-sm text-stone-700">
                        Additional Guests ({plusOnes.length}/{guestData.plus_ones_allowed})
                      </Label>
                      <p className="font-body text-xs italic text-stone-400">
                        Invitados Adicionales
                      </p>
                    </div>
                  </div>
                  {plusOnes.length < guestData.plus_ones_allowed && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addPlusOne}
                      data-testid="add-plus-one-btn"
                      className="text-[#B8A88A] border-[#C9B99A] hover:bg-[#F0EBE3]"
                    >
                      + Add Guest / Agregar
                    </Button>
                  )}
                </div>

                {/* Show notice if household members were pre-filled */}
                {plusOnes.some(po => po.from_household) && (
                  <div className="bg-[#FAF8F5] border border-[#C9B99A] p-4 rounded">
                    <p className="font-body text-sm text-stone-700">
                      We found the household members you provided earlier. Please confirm their attendance and meal preferences.
                    </p>
                    <p className="font-body text-xs italic text-stone-500 mt-1">
                      Encontramos los miembros del hogar que proporcionaste anteriormente. Por favor confirma su asistencia y preferencias de comida.
                    </p>
                  </div>
                )}

                {plusOnes.map((plusOne, index) => (
                  <div key={index} className="bg-white border border-stone-200 p-5 space-y-4">
                    <div className="flex items-center justify-between pb-3 border-b border-stone-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-stone-100 rounded-full flex items-center justify-center">
                          <User size={20} weight="light" className="text-stone-400" />
                        </div>
                        <div>
                          <span className="font-body text-sm font-medium text-stone-700">
                            {plusOne.name || `Guest ${index + 1}`}
                          </span>
                          {plusOne.from_household && (
                            <p className="font-body text-xs text-[#B8A88A]">From household / Del hogar</p>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removePlusOne(index)}
                        className="text-stone-400 hover:text-red-500 p-1"
                      >
                        <X size={18} />
                      </button>
                    </div>
                    
                    {/* Attending toggle for this guest */}
                    <div className="flex items-center gap-3 p-3 bg-stone-50 rounded">
                      <Label className="font-body text-sm text-stone-700 flex-1">
                        Attending? <span className="italic text-stone-400 text-xs">/ ¿Asistirá?</span>
                      </Label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => updatePlusOne(index, 'attending', true)}
                          className={`px-4 py-1.5 text-sm font-body rounded transition-colors ${
                            plusOne.attending !== false
                              ? 'bg-[#C9B99A] text-white'
                              : 'bg-white border border-stone-200 text-stone-600 hover:border-[#C9B99A]'
                          }`}
                        >
                          Yes / Sí
                        </button>
                        <button
                          type="button"
                          onClick={() => updatePlusOne(index, 'attending', false)}
                          className={`px-4 py-1.5 text-sm font-body rounded transition-colors ${
                            plusOne.attending === false
                              ? 'bg-stone-500 text-white'
                              : 'bg-white border border-stone-200 text-stone-600 hover:border-stone-400'
                          }`}
                        >
                          No
                        </button>
                      </div>
                    </div>

                    {plusOne.attending !== false && (
                      <>
                        {/* Welcome Party toggle for this guest */}
                        <div className="flex items-center gap-3 p-3 bg-[#FAF8F5] border border-[#E8E0D0] rounded">
                          <Label className="font-body text-sm text-stone-700 flex-1">
                            Welcome Party (Dec 10)? * <span className="italic text-stone-400 text-xs">/ ¿Fiesta de bienvenida?</span>
                          </Label>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              data-testid={`plus-one-welcome-yes-${index}`}
                              onClick={() => updatePlusOne(index, 'attending_welcome_party', true)}
                              className={`px-4 py-1.5 text-sm font-body rounded transition-colors ${
                                plusOne.attending_welcome_party === true
                                  ? 'bg-[#C9B99A] text-white'
                                  : 'bg-white border border-stone-200 text-stone-600 hover:border-[#C9B99A]'
                              }`}
                            >
                              Yes / Sí
                            </button>
                            <button
                              type="button"
                              data-testid={`plus-one-welcome-no-${index}`}
                              onClick={() => updatePlusOne(index, 'attending_welcome_party', false)}
                              className={`px-4 py-1.5 text-sm font-body rounded transition-colors ${
                                plusOne.attending_welcome_party === false
                                  ? 'bg-stone-500 text-white'
                                  : 'bg-white border border-stone-200 text-stone-600 hover:border-stone-400'
                              }`}
                            >
                              No
                            </button>
                          </div>
                        </div>

                        {/* Beach Day toggle for this guest */}
                        <div className="flex items-center gap-3 p-3 bg-[#FAF8F5] border border-[#E8E0D0] rounded">
                          <Label className="font-body text-sm text-stone-700 flex-1">
                            Beach Day in El Tunco (Sat, Dec 12)? * <span className="italic text-stone-400 text-xs">/ ¿Día de playa?</span>
                          </Label>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              data-testid={`plus-one-beach-yes-${index}`}
                              onClick={() => updatePlusOne(index, 'attending_beach_day', true)}
                              className={`px-4 py-1.5 text-sm font-body rounded transition-colors ${
                                plusOne.attending_beach_day === true
                                  ? 'bg-[#C9B99A] text-white'
                                  : 'bg-white border border-stone-200 text-stone-600 hover:border-[#C9B99A]'
                              }`}
                            >
                              Yes / Sí
                            </button>
                            <button
                              type="button"
                              data-testid={`plus-one-beach-no-${index}`}
                              onClick={() => updatePlusOne(index, 'attending_beach_day', false)}
                              className={`px-4 py-1.5 text-sm font-body rounded transition-colors ${
                                plusOne.attending_beach_day === false
                                  ? 'bg-stone-500 text-white'
                                  : 'bg-white border border-stone-200 text-stone-600 hover:border-stone-400'
                              }`}
                            >
                              No
                            </button>
                          </div>
                        </div>

                        {/* Name Field */}
                        <div className="space-y-2">
                          <Label className="font-body text-sm text-stone-700">
                            Full Name * <span className="italic text-stone-400 text-xs">/ Nombre Completo</span>
                          </Label>
                          <Input
                            data-testid={`plus-one-name-${index}`}
                            value={plusOne.name || ''}
                            onChange={(e) => updatePlusOne(index, 'name', e.target.value)}
                            className="bg-white border-stone-200 focus:border-[#C9B99A]"
                            placeholder="Guest's full name"
                          />
                        </div>

                        {/* Meal Preference */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <ForkKnife size={16} weight="light" className="text-[#B8A88A]" />
                            <Label className="font-body text-sm text-stone-700">
                              Meal Preference * <span className="italic text-stone-400 text-xs">/ Preferencia de Comida</span>
                            </Label>
                          </div>
                          <select
                            data-testid={`plus-one-meal-${index}`}
                            value={plusOne.meal_choice || ''}
                            onChange={(e) => updatePlusOne(index, 'meal_choice', e.target.value)}
                            className="w-full p-3 border border-stone-200 bg-white text-stone-700 font-body text-sm focus:border-[#C9B99A] focus:outline-none rounded"
                          >
                            <option value="">Select meal... / Seleccionar comida...</option>
                            {MEAL_OPTIONS.map((meal) => (
                              <option key={meal.id} value={meal.id}>
                                {meal.name} / {meal.nameEs}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Dietary Restrictions */}
                        <div className="space-y-2">
                          <Label className="font-body text-sm text-stone-700">
                            Dietary Restrictions <span className="italic text-stone-400 text-xs">/ Restricciones Dietéticas (Opcional)</span>
                          </Label>
                          <Input
                            data-testid={`plus-one-dietary-${index}`}
                            value={plusOne.dietary_restrictions || ''}
                            onChange={(e) => updatePlusOne(index, 'dietary_restrictions', e.target.value)}
                            className="bg-white border-stone-200 focus:border-[#C9B99A]"
                            placeholder="Any allergies or restrictions"
                          />
                        </div>

                        {/* Song Request */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <MusicNotes size={16} weight="light" className="text-[#B8A88A]" />
                            <Label className="font-body text-sm text-stone-700">
                              Song Request <span className="italic text-stone-400 text-xs">/ Canción (Opcional)</span>
                            </Label>
                          </div>
                          <Input
                            data-testid={`plus-one-song-${index}`}
                            value={plusOne.song_request || ''}
                            onChange={(e) => updatePlusOne(index, 'song_request', e.target.value)}
                            className="bg-white border-stone-200 focus:border-[#C9B99A]"
                            placeholder="What song will get them dancing?"
                          />
                        </div>
                      </>
                    )}
                    
                    {plusOne.attending === false && (
                      <div className="text-center py-4 text-stone-400">
                        <p className="font-body text-sm">This guest will not be attending</p>
                        <p className="font-body text-xs italic">Este invitado no asistirá</p>
                      </div>
                    )}
                  </div>
                ))}

                {plusOnes.length === 0 && (
                  <p className="font-body text-sm text-stone-400 italic text-center py-4">
                    Click "Add Guest" to include additional guests / Haz clic en "Agregar" para incluir invitados adicionales
                  </p>
                )}
              </div>
            )}

            {/* Message for the Couple */}
            <div className="bg-white border border-stone-200 p-6 space-y-3">
              <Label htmlFor="message" className="font-body text-sm text-stone-700">
                Message for the Couple (Optional) <span className="italic text-stone-400 text-xs">/ Mensaje para la Pareja</span>
              </Label>
              <Textarea
                id="message"
                data-testid="input-message"
                {...register('message')}
                className="bg-white border-stone-200 focus:border-[#C9B99A] focus:ring-[#C9B99A] min-h-[100px]"
                placeholder="Share your well wishes... / Comparte tus buenos deseos..."
              />
            </div>

            {/* Flight Information (Optional) */}
            {attending === 'yes' && (
              <div className="space-y-4 border-t border-stone-200 pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Airplane size={20} weight="light" className="text-[#B8A88A]" />
                    <div>
                      <Label className="font-body text-sm text-stone-700">
                        Flight Information (Optional)
                      </Label>
                      <p className="font-body text-xs italic text-stone-400">
                        Información de Vuelo (Opcional)
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowFlightInfo(!showFlightInfo)}
                    className="text-[#B8A88A] border-[#C9B99A] hover:bg-[#F0EBE3]"
                  >
                    {showFlightInfo ? 'Hide / Ocultar' : 'Add Now / Agregar'}
                  </Button>
                </div>
                
                <p className="font-body text-xs text-stone-500">
                  You can add this now or update it later. This helps us coordinate transportation.
                </p>
                <p className="font-body text-xs italic text-stone-400">
                  Puedes agregar esto ahora o actualizarlo después. Esto nos ayuda a coordinar el transporte.
                </p>

                {showFlightInfo && (
                  <div className="bg-stone-50 border border-stone-200 p-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-body text-sm font-medium text-stone-700 mb-3">
                          Arrival / Llegada
                        </h4>
                        <div className="space-y-3">
                          <div className="space-y-1">
                            <Label className="font-body text-xs text-stone-500">Date / Fecha</Label>
                            <Input
                              type="date"
                              value={flightInfo.arrival_date}
                              onChange={(e) => setFlightInfo({...flightInfo, arrival_date: e.target.value})}
                              className="bg-white border-stone-200 focus:border-[#C9B99A]"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="font-body text-xs text-stone-500">Time / Hora</Label>
                            <Input
                              type="time"
                              value={flightInfo.arrival_time}
                              onChange={(e) => setFlightInfo({...flightInfo, arrival_time: e.target.value})}
                              className="bg-white border-stone-200 focus:border-[#C9B99A]"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="font-body text-xs text-stone-500">Airline / Aerolínea</Label>
                            <Input
                              value={flightInfo.arrival_airline || ''}
                              onChange={(e) => setFlightInfo({...flightInfo, arrival_airline: e.target.value})}
                              className="bg-white border-stone-200 focus:border-[#C9B99A]"
                              placeholder="e.g., American Airlines"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="font-body text-xs text-stone-500">Flight # / Número de Vuelo</Label>
                            <Input
                              value={flightInfo.arrival_flight}
                              onChange={(e) => setFlightInfo({...flightInfo, arrival_flight: e.target.value})}
                              className="bg-white border-stone-200 focus:border-[#C9B99A]"
                              placeholder="e.g., AA1234"
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-body text-sm font-medium text-stone-700 mb-3">
                          Departure / Salida
                        </h4>
                        <div className="space-y-3">
                          <div className="space-y-1">
                            <Label className="font-body text-xs text-stone-500">Date / Fecha</Label>
                            <Input
                              type="date"
                              value={flightInfo.departure_date}
                              onChange={(e) => setFlightInfo({...flightInfo, departure_date: e.target.value})}
                              className="bg-white border-stone-200 focus:border-[#C9B99A]"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="font-body text-xs text-stone-500">Time / Hora</Label>
                            <Input
                              type="time"
                              value={flightInfo.departure_time}
                              onChange={(e) => setFlightInfo({...flightInfo, departure_time: e.target.value})}
                              className="bg-white border-stone-200 focus:border-[#C9B99A]"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="font-body text-xs text-stone-500">Airline / Aerolínea</Label>
                            <Input
                              value={flightInfo.departure_airline || ''}
                              onChange={(e) => setFlightInfo({...flightInfo, departure_airline: e.target.value})}
                              className="bg-white border-stone-200 focus:border-[#C9B99A]"
                              placeholder="e.g., United Airlines"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="font-body text-xs text-stone-500">Flight # / Número de Vuelo</Label>
                            <Input
                              value={flightInfo.departure_flight}
                              onChange={(e) => setFlightInfo({...flightInfo, departure_flight: e.target.value})}
                              className="bg-white border-stone-200 focus:border-[#C9B99A]"
                              placeholder="e.g., UA5678"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pt-2">
                      <input
                        type="checkbox"
                        id="needs_transportation"
                        checked={flightInfo.needs_transportation}
                        onChange={(e) => setFlightInfo({...flightInfo, needs_transportation: e.target.checked})}
                        className="rounded border-stone-300 text-[#C9B99A] focus:ring-[#C9B99A]"
                      />
                      <Label htmlFor="needs_transportation" className="font-body text-sm text-stone-700 cursor-pointer">
                        I need airport transportation <span className="italic text-stone-400 text-xs">/ Necesito transporte del aeropuerto</span>
                      </Label>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Submit */}
            <Button
              type="submit"
              data-testid="submit-rsvp-btn"
              disabled={isLoading}
              className="w-full bg-[#C9B99A] hover:bg-[#B8A88A] text-white py-6 font-body font-medium tracking-wide"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Submitting... / Enviando...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Heart size={20} weight="fill" />
                  {guestData?.has_rsvped ? 'Update RSVP / Actualizar' : 'Submit RSVP / Enviar'}
                </span>
              )}
            </Button>

            {/* Back button */}
            <button
              type="button"
              onClick={() => setStep('lookup')}
              className="w-full text-center font-body text-sm text-stone-500 hover:text-stone-700"
            >
              Not {guestData?.first_name}? Go back / ¿No eres {guestData?.first_name}? Regresar
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
